import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';

import naturalSortBy from '@bibliocommons/natural-sort-by';
import { bindAll } from '@bibliocommons/utils-react';
import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from '@bibliocommons/table';
import { libraryShape } from '@bibliocommons/bc-prop-types';
import ShortFormattedDate from 'app/components/shared/ShortFormattedDate';
import { AVAILABILITY_GROUP_ORDER } from 'app/constants/BibConstants';
import { StackMapLinkContainer } from 'app/components/bibs/shared/StackMap/StackMapLink';
import AvailableGroupIcon from '../AvailableGroupIcon';
import UnAvailableGroupIcon from '../UnAvailableGroupIcon';

import './ItemAvailabilityTable.scss';

const GROUP_ICONS = {
  AVAILABLE_ITEMS: <AvailableGroupIcon />,
  AVAILABLE_BY_REQUEST_ITEMS: <AvailableGroupIcon />,
  LIBRARY_USE_ONLY_ITEMS: <AvailableGroupIcon />,
  RESTRICTED_ACCESS_ITEMS: <AvailableGroupIcon />,
  NOT_AVAILABLE_ITEMS: <UnAvailableGroupIcon />
};

function getAvailabilityGroup(item) {
  const availability = item.get('availability') || Immutable.Map();
  if (availability.get('group')) return availability.get('group');

  // Treat any item without a group as available.
  return item.get('inSiteScope') ? 'AVAILABLE_ITEMS' : 'EXTENDED_AVAILABLE_ITEMS';
}

function renderGroupHeading(rows, fields) {
  const group = rows.first().get('group') || '';

  return (
    <TableRow className="group-heading-row">
      <TableHeading colSpan={fields.length}>
        <h3 className="availability-group-heading">
          <span className="group-icon">{GROUP_ICONS[group.replace(/EXTENDED_|IN_SCOPE_/, '')]}</span>
          <FormattedMessage id={`circ_group_count_${group.toLowerCase()}`} values={{ count: rows.size }} />
        </h3>
      </TableHeading>
    </TableRow>
  );
}

function renderRows(rows, fields) {
  return rows.map(row => (
    <TableRow className="bib-item-row" key={row.get('itemId')}>
      {fields.map(field => (
        <TableCell key={field} label={<FormattedMessage id={field} />} blockLabel>
          {row.getIn(['cells', field])}
        </TableCell>
      ))}
    </TableRow>
  ));
}

export default class ItemAvailabilityTable extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);

    this.state = {
      currentSortField: null,
      currentSortDirection: 'asc'
    };
  }

  getSupportedFields() {
    const { currentLibrary } = this.props;
    const showCollectionInCirc = currentLibrary.get('showCollectionInCirc');
    const showDueDateInCirc = currentLibrary.get('showDueDateInCirc');
    const singleBranch = currentLibrary.get('singleBranch');

    const supportedFields = ['callNumber', 'volume', 'copy', 'availability.libraryStatus'];

    if (showCollectionInCirc) supportedFields.unshift('collection');
    if (showDueDateInCirc) supportedFields.push('dueDate');
    if (!singleBranch) supportedFields.unshift('branch.name');
    return supportedFields;
  }

  getTableItems(supportedFields) {
    const { bibItems } = this.props;
    const { currentSortField, currentSortDirection } = this.state;

    const sortedItems = currentSortField
      ? bibItems.sort(naturalSortBy(currentSortField, currentSortDirection))
      : bibItems;

    return sortedItems.map(item =>
      Immutable.Map({
        itemId: item.get('itemId'),
        group: getAvailabilityGroup(item),
        statusType: item.getIn(['availability', 'statusType']),
        cells: Immutable.Map(supportedFields.map(field => [field, this.renderCellContent(item, field)]))
      })
    );
  }

  handleSort(field) {
    if (this.state.currentSortField === field) {
      this.setState(state => ({
        currentSortDirection: state.currentSortDirection === 'asc' ? 'desc' : 'asc'
      }));
    } else {
      this.setState({
        currentSortField: field,
        currentSortDirection: 'asc'
      });
    }
  }

  renderCellContent(item, field) {
    switch (field) {
      case 'availability.libraryStatus': {
        const status = item.getIn(['availability', 'libraryStatus']);
        return status && <span>{status}</span>;
      }

      case 'dueDate': {
        const dueDate = item.get('dueDate');
        return dueDate && <ShortFormattedDate value={dueDate} />;
      }

      case 'collection': {
        return (
          <>
            <div>{item.get('collection')}</div>
            <StackMapLinkContainer bibItem={item} handleClick={this.props.handleStackMapOpen} />
          </>
        );
      }

      default:
        return item.getIn(field.split('.'));
    }
  }

  renderCaption() {
    const { currentSortField, currentSortDirection } = this.state;
    return (
      currentSortField &&
      currentSortDirection && (
        <FormattedMessage
          id="table_sorted_by"
          values={{
            field: <FormattedMessage id={currentSortField} />,
            direction: <FormattedMessage id={currentSortDirection} />
          }}
        />
      )
    );
  }

  renderTableHeader(fields) {
    const { currentSortField, currentSortDirection } = this.state;

    return (
      <TableHeader>
        {fields.map(field => (
          <TableHeading
            sortable
            sortDirection={currentSortField === field ? currentSortDirection : undefined}
            handleSort={() => this.handleSort(field)}
            key={field}
          >
            <FormattedMessage id={field} />
          </TableHeading>
        ))}
      </TableHeader>
    );
  }

  render() {
    const supportedFields = this.getSupportedFields();
    const tableItems = this.getTableItems(supportedFields);

    const groups = tableItems
      .groupBy(row => row.get('group'))
      .toList()
      .sortBy(rows => AVAILABILITY_GROUP_ORDER.indexOf(rows.first().get('group')));

    // Remove columns with no data.
    const fields = supportedFields.filter(field => tableItems.some(row => !!row.getIn(['cells', field])));

    return (
      <div className="cp-item-availability-table">
        <Table caption={this.renderCaption()}>
          {this.renderTableHeader(fields)}
          {groups.map(rows => (
            <TableBody key={rows.first().get('group')}>
              {renderGroupHeading(rows, fields)}
              {renderRows(rows, fields)}
            </TableBody>
          ))}
        </Table>
      </div>
    );
  }
}

ItemAvailabilityTable.propTypes = {
  bibItems: ImmutablePropTypes.list.isRequired,
  currentLibrary: libraryShape.isRequired,
  handleStackMapOpen: PropTypes.func.isRequired
};
